import { useAppSelector } from '@hooks/app/redux';
import { getNextPageParam } from '@lib/utils/reactQuery/infinityQueryHelpers';
import { getPatientReceipts } from '@services/external/e-prescriptions.api';
import React from 'react';
import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query';
import { GetReceiptsParams } from 'types/e-prescription.types';

const PAGE_SIZE = 20;

type QParams = Omit<GetReceiptsParams, 'page' | 'pageSize'> & { providerId: string };

async function handler(page: number = 1, params: QParams) {
  const resData = await getPatientReceipts(params.providerId, {
    page,
    pageSize: PAGE_SIZE,
    ...params,
  });

  return resData.data.data;
}

type TQueryData = Awaited<ReturnType<typeof handler>>;

function useReceiptsList(
  clientId: string,
  params: Omit<QParams, 'providerId' | 'clientId'> = { direction: 'DESC', sortBy: 'createdAt' },
  options?: Omit<UseInfiniteQueryOptions<TQueryData, any, TQueryData, TQueryData, any>, 'queryKey'>,
) {
  const providerId = useAppSelector((state) => state.users.currentProvider?.id)!;

  const { data, ...rest } = useInfiniteQuery(
    ['prescriptions:receipts-list', params, providerId, clientId],
    ({ pageParam }) => handler(pageParam, { ...params, providerId, clientId }),
    {
      ...options,
      getNextPageParam,
    },
  );

  const flattenData = React.useMemo(() => {
    return data?.pages.flatMap((p) => p.items) ?? [];
  }, [data]);

  return { data: flattenData, original: data, ...rest };
}

export default useReceiptsList;
